/**
 * Shared hook for schedule data management
 * Used by Owner and Manager schedule screens
 */

import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { firestore } from '@/lib/firebase/client';
import { ScheduleWithNames } from '@/types/models';


interface UseScheduleDataOptions {
  tenantId: string;
  role: 'owner' | 'manager';
  allowedLocationIds?: string[]; // For manager filtering
  filterFutureOnly?: boolean; // Filter schedules >= today
}

interface Branch {
  id: string;
  name: string;
}

interface Employee {
  id: string;
  name: string;
}

export function useScheduleData({ tenantId, role, allowedLocationIds, filterFutureOnly = false }: UseScheduleDataOptions) {
  const [schedules, setSchedules] = useState<ScheduleWithNames[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch branches
      const branchesRef = collection(firestore, 'branches');
      const branchesQuery = query(branchesRef, where('tenantId', '==', tenantId));
      const branchesSnapshot = await getDocs(branchesQuery);
      let branchesData = branchesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      })) as Branch[];

      // Filter branches for managers
      if (role === 'manager' && allowedLocationIds && allowedLocationIds.length > 0) {
        branchesData = branchesData.filter(b => allowedLocationIds.includes(b.id));
      }

      // Fetch schedules
      const schedulesRef = collection(firestore, 'schedules');
      const schedulesQuery = query(schedulesRef, where('tenantId', '==', tenantId));
      const schedulesSnapshot = await getDocs(schedulesQuery);
      let allSchedules = schedulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ScheduleWithNames[];

      // Filter by date if requested
      if (filterFutureOnly) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        allSchedules = allSchedules.filter(s => 
          new Date(s.startTimestamp) >= today
        );
      }

      // Filter schedules for managers
      if (role === 'manager' && allowedLocationIds && allowedLocationIds.length > 0) {
        allSchedules = allSchedules.filter(s => 
          allowedLocationIds.includes(s.branchId || s.storeId || '')
        );
      }

      // Sort by startTimestamp
      allSchedules.sort((a, b) => 
        new Date(a.startTimestamp).getTime() - new Date(b.startTimestamp).getTime()
      );

      // Fetch employees
      const usersRef = collection(firestore, 'users');
      const usersQuery = query(
        usersRef, 
        where('tenantId', '==', tenantId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      // Filter for employees and managers on the client side
      const employeesData = usersSnapshot.docs
        .filter(doc => {
          const role = doc.data().role;
          return role === 'employee' || role === 'manager';
        })
        .map(doc => ({
          id: doc.id,
          name: doc.data().name
        })) as Employee[];

      // Enrich schedules with names
      const enrichedSchedules = allSchedules.map(schedule => ({
        ...schedule,
        branchName: branchesData.find(b => b.id === (schedule.branchId || schedule.storeId))?.name,
        employeeNames: schedule.employeeIds?.map(
          id => employeesData.find(e => e.id === id)?.name || 'Unknown'
        ) || [],
      }));

      setSchedules(enrichedSchedules);
      setBranches(branchesData);
      setEmployees(employeesData);
    } catch (err: any) {
      console.error('Error fetching schedule data:', err);
      const errorMessage = err.message || 'Failed to fetch schedule data';
      setError(errorMessage);
      
      // Provide more helpful error message
      if (errorMessage.includes('index')) {
        toast.error('Database index missing. Please contact support.');
      } else if (errorMessage.includes('permission')) {
        toast.error('Permission denied. Please check your access rights.');
      } else if (errorMessage.includes('network')) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(`Failed to load schedules: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenantId, role, allowedLocationIds?.join(','), filterFutureOnly]);

  return {
    schedules,
    branches,
    employees,
    loading,
    error,
    refetch: fetchData
  };
}
